import { ICreatorGatewayDetails } from "repoV2/features/CheckoutPages/utils/apis/PriceDetailsApi/IPriceDetailsApi.interface";
import {
    DISCOUNT_OFF_TYPES,
    LISTING_TYPES,
} from "repoV2/features/Listing/Listing.constants";
import { getActualBasePrice } from "repoV2/features/Listing/utils/getActualBasePrice";
import { IExlySearchableDropdownOption } from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdown/ExlySearchableDropdown.interfaces";
import { GATEWAYS_ENUM, RAZOR_PAY_SOURCE_URL } from "../Payments.constants";
import {
    IActivePaymentGateways,
    IPaymentGatewayConfigArray,
    IStripeConfigGenerationKeys,
} from "../modules/PaymentGateways/IPaymentGateways.interface";

/**
 * @param creatorGatewayDetails Gateway details
 * @returns custom stripe key
 */
export const getCustomStripeKey = (
    creatorGatewayDetails?: ICreatorGatewayDetails[]
) => {
    if (!creatorGatewayDetails) return "";
    const customStripeConfig =
        (
            (creatorGatewayDetails || []).find(
                i => i.gateway === GATEWAYS_ENUM.STRIPE
            ) || {}
        ).config || {};

    return customStripeConfig.published_key;
};

/**
 * This function returns the array of config of all active payment gateways
 * @param activePaymentGateways Array of strings with the list of active payment gateway
 * @param data props for creating config of individual gateways
 * @returns array of payment gateways config
 */
export const getPaymentGatewayConfigArray = (
    activePaymentGateways: IActivePaymentGateways,
    data: {
        stripeData: IStripeConfigGenerationKeys;
    }
) => {
    const paymentGatewayConfig: IPaymentGatewayConfigArray = [];
    activePaymentGateways.forEach(gateway => {
        if (gateway === GATEWAYS_ENUM.STRIPE) {
            const {
                creatorGatewayDetails = [],
                isInternationalCreator,
                stripeConnectId,
                stripeFont,
            } = data?.stripeData;

            paymentGatewayConfig.push([
                gateway,
                {
                    stripePublishKey:
                        getCustomStripeKey(creatorGatewayDetails) || "",
                    isInternationalCreator,
                    stripeConnectId,
                    stripeFont,
                },
            ]);
        } else if (gateway === GATEWAYS_ENUM.RAZORPAY) {
            paymentGatewayConfig.push([
                gateway,
                {
                    sourceUrl: RAZOR_PAY_SOURCE_URL,
                },
            ]);
        }
    });

    return paymentGatewayConfig;
};

/**
 * used to calculate totalAmount and tax amount components form basePrice
 * @param{number} param0.basePrice - final basePrice that the user has to pay after all offers(LTO) and discounts are subtracted
 * @param{number} param0.creatorTaxPercentage GST for indian creators or tax for international creators.
 * @param{number} param0.exlyIHFPercentage Internet Handling fees taken by Exly.
 * @param{number} param0.exlyGSTPercentage GST for Exly platform.
 * @returns creatorTaxAmount - tax to be paid by creator to Govt.
 * @returns exlyIHFAmount - service charges to be paid to Exly
 * @returns exlyTaxAmount - tax to be paid by Exly on service charges it takes
 * @returns totalAmount - sum of basePrice and all above things
 */
export const getAmountComponentsFromBasePrice = ({
    basePrice,
    creatorTaxPercentage,
    exlyIHFPercentage,
    exlyGSTPercentage,
    totalOrderBumpsValueWithTaxes = 0,
    // TODO: figure out if OrderBumps calculation should be part of this function or a separate one
    totalOrderBumpsIHFValue = 0,
    totalOrderBumpsIHFValueWithExlyTaxes = 0,
    shippingValueWithTaxes = 0,
}: {
    basePrice: number;
    creatorTaxPercentage: number; // GST for indian creators or tax for international creators.
    exlyIHFPercentage: number; // Internet Handling fees taken by Exly.
    exlyGSTPercentage: number; // GST for Exly platform.
    totalOrderBumpsValueWithTaxes?: number; // Total order bumps vaues inclusive of their respective taxes
    totalOrderBumpsIHFValue?: number; // Total IHF Value of order bumps
    totalOrderBumpsIHFValueWithExlyTaxes?: number; // Total IHF value of order bumps including EXLY GST
    shippingValueWithTaxes?: number; // shipping value with taxes
}) => {
    // Tax to be paid by creator on the `basePrice`.
    const creatorTaxAmount = (basePrice * creatorTaxPercentage) / 100;

    const basePriceWithCreatorTax = basePrice + creatorTaxAmount;

    // Exly service charges on `basePrice including taxes + shipping charge with taxes * exlyIHFPercentage`
    // Note: this will be zero if creator chooses to bear these charges by selecting
    // `I will bear the booking fee` in listing edit flow
    const primaryListingIHFAmount =
        ((basePriceWithCreatorTax + shippingValueWithTaxes) *
            exlyIHFPercentage) /
        100;

    const exlyIHFAmount = primaryListingIHFAmount + totalOrderBumpsIHFValue;

    // Tax to be paid by Exly on the service charges(exlyIHFAmount) it takes.
    const exlyTaxAmount =
        (primaryListingIHFAmount * exlyGSTPercentage) / 100 +
        totalOrderBumpsIHFValueWithExlyTaxes;

    const totalAmount =
        basePriceWithCreatorTax +
        totalOrderBumpsValueWithTaxes +
        shippingValueWithTaxes +
        exlyIHFAmount +
        exlyTaxAmount;

    return {
        creatorTaxAmount,
        exlyIHFAmount,
        exlyTaxAmount,
        totalAmount,
    };
};

/**
 * To calculate maximum discount amount on `basePrice`.
 * Usually, for actual payment calculation, we should call this fuction
 * on `basePrice` after all offers(LTO) are applied and all multipliers
 * are considered.
 * @param basePrice - the final base price after taking into account the
 * LTO offers and multipliers for customers etc
 * @param discountOffType - discount type, can be an absolute price, or in percentage
 * @param discountValue - discount value to be applied on base price
 * @param discountIncludesGst - boolean to whether the discount is including GST
 * @param creatorTaxPercentage - The tax/gst percentage to remove frm discount amount
 * incase discountIncludesGst is true
 */
export const getDiscountAmountOnBasePrice = ({
    basePrice,
    discountOffType,
    discountValue,
    discountIncludesGst,
    creatorTaxPercentage,
}: {
    basePrice: number;
    discountOffType: number;
    discountValue: number;
    discountIncludesGst: boolean;
    creatorTaxPercentage: number; // GST for indian creators or tax for international creators.
}) => {
    let discountOnBasePrice = 0;

    // if discountOffType is %
    if (discountOffType === DISCOUNT_OFF_TYPES.PERCENTAGE) {
        // we can simply take percentage of the basePrice to calculate the discountValue
        if (discountIncludesGst) {
            // In this case,
            // we first remove the tax from our discount percentage
            // mathematically,
            // actualDiscountValue = (discountValue * 100) / (100 + creatorTaxPercentage)
            // Now we apply this actualDiscountValue percentage on our base price
            const actualDiscountValue = getActualBasePrice(
                discountValue,
                creatorTaxPercentage
            );
            discountOnBasePrice = (basePrice * actualDiscountValue) / 100;
        } else {
            // if discount does not include tax(GST for indian creators)
            // we directly apply the discount upon our base price
            discountOnBasePrice = (basePrice * discountValue) / 100;
        }
    } else {
        // if discount is absolute

        // if discount includes tax(GST for indian creators), we need to remove tax value from it to get the base discount value
        if (discountIncludesGst) {
            // In this case,
            // discountValue = base discount price + tax value on base discount price

            // mathematically,
            // discountValue = maxDiscountOnBasePrice + (maxDiscountOnBasePrice * (creatorTaxPercentage / 100));
            // => maxDiscountOnBasePrice = (discountValue * 100) / (100 + creatorTaxPercentage);

            discountOnBasePrice = getActualBasePrice(
                discountValue,
                creatorTaxPercentage
            );
        } else {
            // if discount does not include tax(GST for indian creators),
            // discountValue is equivalent to discountOnBasePrice.
            discountOnBasePrice = discountValue;
        }
    }

    // discount can only be less than or equal to basePrice
    return Math.min(discountOnBasePrice, basePrice);
};

/**
 * To calculate the multiplier to apply on base price.
 * e.g. For a webinar, if user selects multiple slots, then the slot
 * count will be the multiplier as the base price needs to be taken
 * into account for each slot
 * @param listingType - The type of listing
 * @param selectedSessions - Array of selected slots (incase of all listings where it is required - Webinars, Workshops, Classes, Appointments)
 * @returns
 */
export const getListingPriceMultiplier = ({
    listingType,
    selectedSessions: selectedSlotsArg,
    listingQuantity: merchandiseQuantity,
}: {
    listingType: number;
    selectedSessions?: Array<string> | IExlySearchableDropdownOption;
    listingQuantity?: number;
}) => {
    const selectedSessions = selectedSlotsArg || [];
    const selectedQuantity = merchandiseQuantity || 1;

    let selectedSlotsCount = 1; // for calculation purposes... we will show minimum selected count = 1
    if (Array.isArray(selectedSessions)) {
        selectedSlotsCount = selectedSessions.length;
    }
    let multiplier = 1;
    if (listingType === LISTING_TYPES.NO_SCHEDULE) {
        multiplier = 1;
    }

    if (listingType === LISTING_TYPES.WORKSHOPS) {
        multiplier = 1;
    }

    if (listingType === LISTING_TYPES.WEBINAR) {
        multiplier = selectedSlotsCount;
    }

    if (listingType === LISTING_TYPES.APPOINTMENTS) {
        multiplier = selectedSlotsCount;
    }

    if (listingType === LISTING_TYPES.MERCHANDISE) {
        multiplier = selectedQuantity;
    }
    // here we return the maximum of the two, incase multiplier is zero, we have
    // to, for calculation purposes, show minimum selected count as 1, else it
    // makes the listing multiplier 0, which makes the total listing value 0
    return Math.max(multiplier, 1);
};
